import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BookingButton from './BookingButton';

const { FiTrendingUp, FiHeart, FiMessageCircle, FiStar, FiShield } = FiIcons;

const BenefitsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const benefits = [
    {
      icon: FiTrendingUp,
      title: "Catch Up Before Falling Behind",
      description: "Early intervention yields better outcomes than later intervention. Build skills now instead of playing catch-up later.",
      color: "accent-magenta"
    },
    {
      icon: FiHeart,
      title: "Transform Family Mealtimes",
      description: "From food battles to peaceful eating. Address feeding issues, dysphagia, and sensory challenges that make eating difficult.",
      color: "red"
    },
    {
      icon: FiMessageCircle,
      title: "Unlock Their Communication",
      description: "We help them find their voice and connect with their world.",
      color: "blue"
    },
    {
      icon: FiStar,
      title: "Build School Success Early",
      description: "Develop the attention, self-regulation, and learning skills that determine school readiness and beyond.",
      color: "yellow"
    },
    {
      icon: FiShield,
      title: "Peace of Mind for Parents",
      description: "Feel the relief of knowing you're not alone and have an expert guide and a clear plan.",
      color: "green"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      "accent-magenta": "bg-accent-magenta/10 text-accent-magenta border-accent-magenta/20",
      "red": "bg-red-100 text-red-600 border-red-200",
      "blue": "bg-blue-100 text-blue-600 border-blue-200",
      "yellow": "bg-yellow-100 text-yellow-600 border-yellow-200",
      "green": "bg-green-100 text-green-600 border-green-200"
    };
    return colorMap[color] || colorMap["accent-magenta"];
  };

  return (
    <section id="benefits" className="py-20 bg-gradient-to-br from-gray-50 to-accent-light/20" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-serif">
            What Early Intervention Actually <span className="gradient-text">Changes</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
            >
              <div className={`w-16 h-16 ${getColorClasses(benefit.color)} rounded-xl flex items-center justify-center mb-6 border-2`}>
                <SafeIcon icon={benefit.icon} className="h-8 w-8" />
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-4 font-serif">
                {benefit.title}
              </h3>
              
              <p className="text-gray-700 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <BookingButton text="Experience These Changes" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;